import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../supabaseClient'
import ConfirmModal from './ConfirmModal'
import {
  TIPOS_DOCUMENTO_CLIENTE,
  getTipoDocumentoLabel as getTipoDocumentoLabelShared,
  uploadDocumentoCliente,
  validarArquivoDocumento
} from '../services/documentosClienteApi'
import './DocumentosCliente.css'

/**
 * Componente de Gerenciamento de Documentos do Cliente
 * 
 * Permite fazer upload, visualizar e excluir documentos vinculados a um cliente
 * Armazena arquivos no Supabase Storage e metadados na tabela documentos_clientes
 */
const DocumentosCliente = ({ cliente, onClose, tiposFiltrados, apenasPdf = false }) => {
  const tiposDocumento = tiposFiltrados?.length
    ? TIPOS_DOCUMENTO_CLIENTE.filter(t => tiposFiltrados.includes(t.value))
    : TIPOS_DOCUMENTO_CLIENTE
  const tipoInicial = tiposDocumento[0]?.value || 'rg_cnh'

  const [documentos, setDocumentos] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [modal, setModal] = useState({ show: false, message: '', type: '', title: '' })
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, type: 'warning' })
  
  const [formData, setFormData] = useState({
    tipo_documento: tipoInicial,
    observacao: '',
    arquivo: null
  })

  useEffect(() => {
    if (cliente?.id) {
      setFormData(prev => ({ ...prev, tipo_documento: tipoInicial, arquivo: null }))
      loadDocumentos()
    }
  }, [cliente?.id, tipoInicial])

  const showModal = (title, message, type = 'success') => {
    setModal({ show: true, title, message, type })
  }

  const closeModal = () => {
    setModal({ show: false, message: '', type: '', title: '' })
  }

  const loadDocumentos = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('documentos_clientes')
        .select('*')
        .eq('cliente_id', cliente.id)
        .order('data_upload', { ascending: false })

      if (error) throw error
      setDocumentos(data || [])
    } catch (error) {
      console.error('Erro ao carregar documentos:', error)
      showModal('Erro ao Carregar', 'Não foi possível carregar os documentos: ' + error.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const validacao = validarArquivoDocumento(file, { apenasPdf })
      if (!validacao.ok) {
        showModal('Arquivo Inválido', validacao.error, 'error')
        e.target.value = ''
        return
      }
      setFormData(prev => ({ ...prev, arquivo: file }))
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleUpload = async (e) => {
    e.preventDefault()

    if (!formData.arquivo) {
      showModal('Nenhum Arquivo Selecionado', 'Por favor, selecione um arquivo antes de enviar.', 'error')
      return
    }

    try {
      setUploading(true)

      await uploadDocumentoCliente({
        clienteId: cliente.id,
        tipoDocumento: formData.tipo_documento,
        arquivo: formData.arquivo,
        observacao: formData.observacao
      })

      showModal('Sucesso!', 'Documento enviado e salvo com sucesso!', 'success')
      
      // Resetar formulário
      setFormData({
        tipo_documento: 'rg_cnh',
        observacao: '',
        arquivo: null
      })
      document.getElementById('arquivo-input').value = ''
      
      // Recarregar lista de documentos
      loadDocumentos()
    } catch (error) {
      console.error('Erro ao fazer upload:', error)
      if (typeof error?.message === 'string' && error.message.toLowerCase().includes('maximum allowed size')) {
        showModal(
          'Arquivo muito grande',
          'O Supabase recusou o upload porque o bucket ainda está configurado com um limite máximo inferior a 20MB. Ajuste o limite nas configurações do bucket antes de tentar novamente.',
          'error'
        )
      } else {
        showModal('Erro no Upload', error?.message || 'Não foi possível enviar o documento.', 'error')
      }
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (documento) => {
    setConfirmModal({
      show: true,
      title: 'Excluir Documento',
      message: `Tem certeza que deseja excluir o documento "${documento.nome_arquivo}"? Esta ação não pode ser desfeita.`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null })
        
        try {
          setLoading(true)

          // Extrair caminho do arquivo da URL
          const url = new URL(documento.caminho_arquivo)
          const pathParts = url.pathname.split('/')
          const filePath = pathParts.slice(pathParts.indexOf('documentos-clientes') + 1).join('/')

          // Deletar arquivo do Storage
          const { error: storageError } = await supabase.storage
            .from('documentos-clientes')
            .remove([filePath])

          if (storageError) {
            console.warn('Erro ao deletar arquivo do storage:', storageError)
          }

          // Deletar registro do banco
          const { error: dbError } = await supabase
            .from('documentos_clientes')
            .delete()
            .eq('id', documento.id)

          if (dbError) throw dbError

          showModal('Sucesso!', 'Documento excluído com sucesso!', 'success')
          loadDocumentos()
        } catch (error) {
          console.error('Erro ao excluir documento:', error)
          showModal('Erro ao Excluir', 'Não foi possível excluir o documento: ' + error.message, 'error')
        } finally {
          setLoading(false)
        }
      }
    })
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTipoDocumentoLabel = (tipo) => getTipoDocumentoLabelShared(tipo)

  if (typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <div className="documentos-overlay">
      <div className="documentos-modal">
        <div className="documentos-header">
          <h2>📎 Documentos - {cliente?.nome_completo}</h2>
          <button onClick={onClose} className="btn-close-modal">✕</button>
        </div>

        <div className="documentos-content">
          {/* Formulário de Upload */}
          <div className="upload-section">
            <h3>📤 Enviar Novo Documento</h3>
            <form onSubmit={handleUpload} className="upload-form">
              <div className="form-group">
                <label htmlFor="tipo_documento">Tipo de Documento *</label>
                <select
                  id="tipo_documento"
                  name="tipo_documento"
                  value={formData.tipo_documento}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                >
                  {tiposDocumento.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="arquivo-input">Arquivo * {apenasPdf ? '(PDF - Máx: 20MB)' : '(PDF, JPG, PNG, WebP - Máx: 20MB)'}</label>
                <input
                  type="file"
                  id="arquivo-input"
                  onChange={handleFileChange}
                  accept={apenasPdf ? '.pdf,application/pdf' : '.pdf,.jpg,.jpeg,.png,.webp'}
                  required
                  className="form-control file-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="observacao">Observação</label>
                <textarea
                  id="observacao"
                  name="observacao"
                  value={formData.observacao}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Observações sobre o documento..."
                  className="form-control"
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={uploading || !formData.arquivo}
              >
                {uploading ? '⏳ Enviando...' : '📤 Enviar Documento'}
              </button>
            </form>
          </div>

          {/* Lista de Documentos */}
          <div className="documentos-list-section">
            <h3>📋 Documentos Anexados ({documentos.length})</h3>
            
            {loading && <div className="loading">Carregando documentos...</div>}
            
            {!loading && documentos.length === 0 && (
              <div className="empty-state-docs">
                <p>📄 Nenhum documento anexado ainda.</p>
              </div>
            )}

            {!loading && documentos.length > 0 && (
              <div className="documentos-table-container">
                <div className="documentos-table-wrapper">
                  <table className="documentos-table">
                    <thead>
                      <tr>
                        <th>Tipo de Documento</th>
                        <th>Nome do Arquivo</th>
                        <th>Data de Upload</th>
                        <th>Observação</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentos.map(doc => (
                        <tr key={doc.id}>
                          <td className="doc-tipo">
                            <span className="tipo-badge">
                              {getTipoDocumentoLabel(doc.tipo_documento)}
                            </span>
                          </td>
                          <td className="doc-arquivo">
                            <span className="arquivo-nome">{doc.nome_arquivo}</span>
                          </td>
                          <td className="doc-data">{formatDate(doc.data_upload)}</td>
                          <td className="doc-observacao">
                            {doc.observacao ? (
                              <span className="obs-text" title={doc.observacao}>
                                {doc.observacao.length > 50 
                                  ? doc.observacao.substring(0, 50) + '...' 
                                  : doc.observacao}
                              </span>
                            ) : (
                              <span className="sem-obs">-</span>
                            )}
                          </td>
                          <td className="doc-acoes">
                            <div className="acoes-group-docs">
                              <a
                                href={doc.caminho_arquivo}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-view"
                                title="Visualizar documento"
                              >
                                👁️
                              </a>
                              <a
                                href={doc.caminho_arquivo}
                                download={doc.nome_arquivo}
                                className="btn btn-sm btn-download"
                                title="Baixar documento"
                              >
                                ⬇️
                              </a>
                              <button
                                onClick={() => handleDelete(doc)}
                                className="btn btn-sm btn-delete-doc"
                                title="Excluir documento"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="documentos-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Fechar
          </button>
        </div>

        {/* Modal de Mensagens */}
        {modal.show && (
          <div className="modal-overlay-mensagem" onClick={closeModal}>
            <div 
              className={`modal-mensagem modal-${modal.type}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-mensagem-header">
                <h3>{modal.title}</h3>
                <button onClick={closeModal} className="btn-close-modal-msg">✕</button>
              </div>
              <div className="modal-mensagem-body">
                <div className={`icon-${modal.type}`}>
                  {modal.type === 'success' ? '✅' : '❌'}
                </div>
                <p>{modal.message}</p>
              </div>
              <div className="modal-mensagem-footer">
                <button onClick={closeModal} className="btn btn-primary">
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Confirmação */}
        <ConfirmModal
          show={confirmModal.show}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })}
          confirmText="Sim, Excluir"
          cancelText="Cancelar"
        />
      </div>
    </div>,
    document.body
  )
}

export default DocumentosCliente

